'use client';
import { useState } from 'react';

import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';

import { CommentsInfo } from './comments-info';
import { FeedbackToast } from '../../../../components/feedback-toast';

import { ActionButton } from '../../../../components/action-button';
import { UserIcon } from '../../../../components/icons';
import CommentList from './comments-list/comments-list';
import { useEnv } from 'config/env-provider';

const MAX_COMMENT_LENGTH = 500;
const MAX_NICKNAME_LENGTH = 50;

export const Comments = ({ databaseId, comments }) => {
  const { handleSubmit, control, watch, reset } = useForm({
    defaultValues: {
      username: '',
      authorName: '',
      content: '',
    },
  });

  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [isChangeNicknameVisible, setChangeNicknameVisible] = useState(false);

  const commentContent = watch('content') || '';
  const showSubmitButton = commentContent.trim().length > 0;
  const { baseUrl } = useEnv();

  const handleReset = () => {
    setCommentSubmitted(false);
    setSubmitError(false);
    setIsPending(false);
  };

  const onSubmit = async (data) => {
    // Honeypot field for spammers
    if (data.username) return;

    setIsPending(true);
    setSubmitError(false);

    const now = new Date();
    const payload = {
      commentOn: databaseId,
      author: data.authorName?.trim() || 'Gość',
      content: data.content,
      date: new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString(),
    };

    try {
      const response = await fetch(`${baseUrl}/api/submit-comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      setCommentSubmitted(true);
      reset();
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsPending(false);
    }
  };

  const commentsNodes = comments?.nodes || [];

  return (
    <section className="w-full px-0">
      {!commentSubmitted ? <CommentsInfo /> : <div className="h-4 px-0" aria-hidden="true" />}

      {submitError && (
        <p className="flex items-center text-white px-0">
          Nie udało się wysłać komentarza. Spróbuj ponownie.{' '}
          <button
            type="button"
            onClick={handleReset}
            disabled={isPending}
            className="ml-2 flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-1 transition-opacity hover:opacity-80 disabled:opacity-50 px-0 focus:outline-none"
            aria-label="Odśwież"
          >
            <svg className="h-5 w-5 fill-current text-primary px-0" viewBox="0 0 24 24">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.24c.73 2.28 2.81 4 5.24 4 3.04 0 5.5-2.46 5.5-5.5 0-1.52-.62-2.89-1.61-3.89l-1.39 1.39zM12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
            </svg>
          </button>
        </p>
      )}

      {commentSubmitted && (
        <FeedbackToast
          message="Dziękujemy! Twój komentarz zostanie wyświetlony po akceptacji administratora."
          onClose={() => setCommentSubmitted(false)}
          className="mb-12 px-0"
        />
      )}

      {!submitError && !commentSubmitted && (
        <form className="px-0" onSubmit={handleSubmit(onSubmit)} autoComplete="off" aria-label="Zostaw komentarz">
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <input {...field} type="text" className="hidden" aria-label="Nazwa użytkownika" tabIndex={-1} />
            )}
          />

          <div className="flex px-0">
            <div className="mr-2 px-0">
              <UserIcon />
            </div>

            <div className="w-full px-0">
              {!isChangeNicknameVisible ? (
                <div className="px-0">
                  <span className="mr-2 text-[14px] leading-[18px] text-primary px-0">Gość</span>
                  <span
                    className="cursor-pointer text-[14px] text-[#ec3654] hover:underline px-0"
                    onClick={() => setChangeNicknameVisible(true)}
                  >
                    Zmień
                  </span>
                </div>
              ) : (
                <Controller
                  name="authorName"
                  control={control}
                  render={({ field }) => (
                    <div className="relative mb-4 w-full px-0">
                      <input
                        {...field}
                        type="text"
                        disabled={isPending}
                        placeholder="Gość"
                        maxLength={MAX_NICKNAME_LENGTH}
                        aria-label="Gość"
                        className="w-full border-b border-transparent bg-transparent text-white outline-none transition-colors placeholder:text-gray-500 focus:border-white disabled:opacity-50 px-0"
                      />
                    </div>
                  )}
                />
              )}

              <div className="mt-2 flex gap-[6px] px-0">
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <div className="relative mb-4 w-full px-0">
                      <textarea
                        {...field}
                        disabled={isPending}
                        placeholder="Co myślisz o tym artykule?"
                        maxLength={MAX_COMMENT_LENGTH}
                        aria-label="Co myślisz o tym artykule?"
                        rows={3}
                        className={clsx(
                          'w-full resize-none rounded-[4px] border border-solid border-[#444] bg-transparent p-2 text-[12px] leading-[16px] text-primary outline-none transition-colors px-0',
                          'xmd:text-[14px] xmd:leading-[18px]',
                          'focus:border-gray-500 hover:border-gray-500',
                          'disabled:border-gray-500 disabled:bg-[rgba(128,128,128,0.1)] disabled:text-gray-500',
                        )}
                      />
                      <span className="absolute -bottom-4 right-0 text-xs text-tertiary px-0">
                        {commentContent.length}/{MAX_COMMENT_LENGTH}
                      </span>
                    </div>
                  )}
                />

                {showSubmitButton && (
                  <div className="mb-6 flex px-0">
                    <ActionButton
                      type="submit"
                      text="Wyślij"
                      loading={isPending}
                      className="max-h-[36px] min-w-[36px] self-end p-0 xmd:px-3 xmd:py-2"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      )}

      {commentsNodes.length > 0 && <CommentList commentsNodes={commentsNodes} />}
    </section>
  );
};
